import ExperimentService from 'business/services/ExperimentService';
import { ExperimentReportJobStartRequestDTO } from 'business/models/Models';
import { EJobStatus } from 'business/models/CustomModels';
import { BackgroundJobDTO } from 'components/backgroundjobs/backgroundJobs';
import { generateUUID, showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { ExperimentReportOptionsDTO } from './ExperimentReportOptions';
import { openOrSavePdfBlob } from './utils';

export type ExperimentReportBackgroundTaskParam = {
  experimentIds: number[];
  reportOptions: ExperimentReportOptionsDTO;
  saveReport: boolean;
  fileName: string;
  onPopupBlocked?: () => void;
};

const pendingReportJobs = new Map<string, { onPopupBlocked?: () => void }>();
const completingReportJobs = new Set<string>();

export async function startExperimentReportBackgroundJob(
  param: ExperimentReportBackgroundTaskParam,
): Promise<void> {
  const jobId = generateUUID();
  pendingReportJobs.set(jobId, { onPopupBlocked: param.onPopupBlocked });

  const captureProfileIds = param.reportOptions.captureProfiles
    .filter((cp) => cp.selected)
    .map((cp) => cp.id);

  const experimentService = new ExperimentService();
  await experimentService.startExperimentReportJob(
    new ExperimentReportJobStartRequestDTO({
      jobId,
      experimentIds: param.experimentIds,
      captureProfileIds,
      projectInfo: param.reportOptions.projectInfo,
      requiredStocks: param.reportOptions.requiredStocks,
      wellIngredients: param.reportOptions.wellIngredients,
      designReport: param.reportOptions.designReport,
      customLayer: param.reportOptions.customLayer,
      randomLayer: param.reportOptions.randomLayer,
      gridLayer: param.reportOptions.gridLayer,
      proteinLayer: param.reportOptions.proteinLayer,
      screenLayer: param.reportOptions.screenLayer,
      screenDetail: param.reportOptions.screenDetail,
      experiment: param.reportOptions.experiment,
      imaging: param.reportOptions.imaging,
      plates: param.reportOptions.plates,
      dropImages: param.reportOptions.dropImages,
      fileName: param.fileName,
      saveReport: param.saveReport,
    }),
  );
}

export async function completeExperimentReportBackgroundJob(
  job: BackgroundJobDTO,
): Promise<void> {
  const jobId = String(job.jobId);

  if (!pendingReportJobs.has(jobId) || completingReportJobs.has(jobId)) {
    return;
  }

  completingReportJobs.add(jobId);
  const pending = pendingReportJobs.get(jobId);
  pendingReportJobs.delete(jobId);

  try {
    if (job.jobStatus === EJobStatus.Error) {
      const message =
        job.logs?.[job.logs.length - 1] ?? 'Experiment report generation failed.';
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, message);
      return;
    }

    if (job.jobStatus !== EJobStatus.Completed) {
      return;
    }

    const fileName = job.dataObj?.fileName ?? 'Experiment Report';
    const saveReport = job.dataObj?.saveReport ?? false;

    const experimentService = new ExperimentService();
    const fileResponse = await experimentService.getExperimentReportJobResult(jobId);
    openOrSavePdfBlob(fileResponse.data, fileName, saveReport, pending?.onPopupBlocked);
  } catch {
    showAlertSnackBar(
      EAlertType.ERROR,
      ESnackBarPosition.TOP,
      'Unable to download the generated experiment report.',
    );
  } finally {
    completingReportJobs.delete(jobId);
  }
}
